import {
  QUEUE_PAGE_LIMIT,
  QueueDisposition,
  UnroutedExchangeSummary,
  UnroutedQueueList,
} from '../types/ConnectorAdmin.type';

/**
 * Pure cache transforms for the per-disposition queue lists (INFRA-10.2
 * item 4). The admin hook (`useConnectionApi.ts`) applies these to every
 * cached `UnroutedQueueList` on a mutation success — patch from response
 * data, never invalidate-and-refetch. Pure so the page-bound and totals
 * arithmetic are testable without React Query
 * (`testing/connection-api/unroutedQueueCache.spec.ts`).
 */

/**
 * Assign removed a row from the Unrouted queue (it left for a clinic): drop
 * it from the cache that holds it and decrement the unrouted total in EVERY
 * cached copy — each response carries all three totals.
 */
export const applyQueueAssign = (
  cache: UnroutedQueueList,
  exchangeId: string
): UnroutedQueueList => ({
  ...cache,
  rows:
    cache.disposition === 'unrouted'
      ? cache.rows.filter(r => r.exchangeId !== exchangeId)
      : cache.rows,
  totals: { ...cache.totals, unrouted: Math.max(0, cache.totals.unrouted - 1) },
});

/**
 * Close/reopen MOVED a row between queues: drop it from the `from` cache,
 * insert the patched copy into the `to` cache in `receivedAt` order, and
 * shift both totals in every cached copy. The insert holds the server's page
 * contract — at most `limit` rows, newest first — so a full destination
 * evicts its oldest row (exactly what the next real fetch would return) and
 * a moved row older than the visible page is counted but not shown.
 */
export const applyQueueMove = (
  cache: UnroutedQueueList,
  {
    from,
    to,
    patched,
    limit = QUEUE_PAGE_LIMIT,
  }: {
    from: QueueDisposition;
    to: QueueDisposition;
    /** The moved row, already carrying its new disposition/history/reprocessable. */
    patched: UnroutedExchangeSummary;
    limit?: number;
  }
): UnroutedQueueList => {
  // Not a move — and the arithmetic below would net +0 rows / +0 totals only
  // by accident. The hook re-reads instead of patching in this case.
  if (from === to) return cache;

  const rows =
    cache.disposition === from
      ? cache.rows.filter(r => r.exchangeId !== patched.exchangeId)
      : cache.disposition === to
        ? [...cache.rows.filter(r => r.exchangeId !== patched.exchangeId), patched]
            .sort((a, b) => b.receivedAt - a.receivedAt)
            .slice(0, limit)
        : cache.rows;

  return {
    ...cache,
    rows,
    totals: {
      ...cache.totals,
      [from]: Math.max(0, cache.totals[from] - 1),
      [to]: cache.totals[to] + 1,
    },
  };
};
